import {Link,useLocation} from 'react-router-dom';
import LumaResourceWorkspace, {LumaColumn} from '../../../components/admin/LumaResourceWorkspace';
import AdminTable from '../../../components/admin/AdminTable';
import React, { useState, useEffect, useCallback } from 'react';
import {
    Plus, Sparkles, ArrowUp, ArrowDown, Edit3, Trash2, Video,
    Image as ImageIcon, Eye, EyeOff, Layers, ExternalLink, RefreshCw,
    LayoutTemplate, Grid, Award, LayoutList
} from 'lucide-react';
import {
    BannerSlide,
    fetchBannerSlidesAdmin,
    reorderBannerSlides,
    updateBannerSlide,
    deleteBannerSlide,
} from '../../../services/landing/bannerSlideService';
import {
    LandingEspacio,
    LandingExperiencia,
    LandingSplitSectionItem,
    fetchEspaciosAdmin,
    reorderEspacios,
    updateEspacio,
    deleteEspacio,
    fetchExperienciaAdmin,
    reorderExperiencia,
    updateExperiencia,
    deleteExperiencia,
    fetchSplitSectionsAdmin,
    reorderSplitSections,
    updateSplitSection,
    deleteSplitSection,
} from '../../../services/landing/landingSectionsService';

import AddBannerSlideModal from '../../../components/BannerHero/AddBannerSlideModal';
import EditBannerSlideModal from '../../../components/BannerHero/EditBannerSlideModal';
import DeleteBannerSlideModal from '../../../components/BannerHero/DeleteBannerSlideModal';
import { EspacioModal } from '../../../components/LandingSections/EspacioModal';
import { ExperienciaModal } from '../../../components/LandingSections/ExperienciaModal';
import { SplitSectionModal } from '../../../components/LandingSections/SplitSectionModal';
import { DeleteLandingItemModal } from '../../../components/LandingSections/DeleteLandingItemModal';
import Loading from '../../../components/Loading';

type ActiveTab = 'hero' | 'espacios' | 'experiencia' | 'split';

export const BannerHeroAdminPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<ActiveTab>('hero');
    const {pathname}=useLocation();
    useEffect(()=>{const tabs:Record<string,ActiveTab>={diapositivas:'hero',galeria:'espacios',experiencia:'experiencia',secciones:'split'};setActiveTab(tabs[pathname.split('/').pop()||'']||'hero');},[pathname]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // ═══ TAB 1: HERO SLIDES ═══
    const [slides, setSlides] = useState<BannerSlide[]>([]);
    const [isAddSlideOpen, setIsAddSlideOpen] = useState(false);
    const [isEditSlideOpen, setIsEditSlideOpen] = useState(false);
    const [isDeleteSlideOpen, setIsDeleteSlideOpen] = useState(false);
    const [selectedSlide, setSelectedSlide] = useState<BannerSlide | null>(null);

    // ═══ TAB 2: ESPACIOS (ACORDEÓN) ═══
    const [espacios, setEspacios] = useState<LandingEspacio[]>([]);
    const [isEspacioModalOpen, setIsEspacioModalOpen] = useState(false);
    const [selectedEspacio, setSelectedEspacio] = useState<LandingEspacio | null>(null);
    const [isDeleteEspacioOpen, setIsDeleteEspacioOpen] = useState(false);

    // ═══ TAB 3: CARRUSEL EXPERIENCIA ═══
    const [experiencias, setExperiencias] = useState<LandingExperiencia[]>([]);
    const [isExperienciaModalOpen, setIsExperienciaModalOpen] = useState(false);
    const [selectedExperiencia, setSelectedExperiencia] = useState<LandingExperiencia | null>(null);
    const [isDeleteExperienciaOpen, setIsDeleteExperienciaOpen] = useState(false);

    // ═══ TAB 4: SECCIONES DIVIDIDAS (SPLIT) ═══
    const [splitSections, setSplitSections] = useState<LandingSplitSectionItem[]>([]);
    const [isSplitModalOpen, setIsSplitModalOpen] = useState(false);
    const [selectedSplit, setSelectedSplit] = useState<LandingSplitSectionItem | null>(null);
    const [isDeleteSplitOpen, setIsDeleteSplitOpen] = useState(false);

    const showMessage = (type: 'success' | 'error', text: string) => {
        setMessage({ type, text });
        setTimeout(() => setMessage(null), 4000);
    };

    // Load data depending on active tab or all
    const loadAll = useCallback(async () => {
        try {
            setLoading(true);
            const [slidesData, espaciosData, expData, splitData] = await Promise.all([
                fetchBannerSlidesAdmin().catch(() => []),
                fetchEspaciosAdmin().catch(() => []),
                fetchExperienciaAdmin().catch(() => []),
                fetchSplitSectionsAdmin().catch(() => []),
            ]);
            setSlides(slidesData.sort((a, b) => a.orden - b.orden));
            setEspacios(espaciosData.sort((a, b) => a.orden - b.orden));
            setExperiencias(expData.sort((a, b) => a.orden - b.orden));
            setSplitSections(splitData.sort((a, b) => a.orden - b.orden));
        } catch (error) {
            /* Browser diagnostics omitted. */
            showMessage('error', 'Error al sincronizar datos del landing');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    // ──────────────────────────────────────────
    // HANDLERS HERO SLIDES
    // ──────────────────────────────────────────
    const handleToggleSlideActivo = async (slide: BannerSlide) => {
        try {
            const formData = new FormData();
            formData.append('activo', String(!slide.activo));
            await updateBannerSlide(slide.id, formData);
            setSlides((prev) =>
                prev.map((s) => (s.id === slide.id ? { ...s, activo: !s.activo } : s))
            );
            showMessage('success', `Slide #${slide.orden} ${!slide.activo ? 'activado' : 'desactivado'}.`);
        } catch {
            showMessage('error', 'No se pudo cambiar el estado del slide.');
        }
    };

    const handleMoveSlide = async (index: number, direction: 'up' | 'down') => {
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= slides.length) return;
        setActionLoading(true);
        const newSlides = [...slides];
        const temp = newSlides[index];
        newSlides[index] = newSlides[targetIndex];
        newSlides[targetIndex] = temp;
        const reorderPayload = newSlides.map((item, idx) => ({ id: item.id, orden: idx + 1 }));
        setSlides(newSlides.map((item, idx) => ({ ...item, orden: idx + 1 })));
        try {
            await reorderBannerSlides(reorderPayload);
            showMessage('success', 'Orden de diapositivas actualizado.');
        } catch {
            showMessage('error', 'Error al guardar el nuevo orden.');
            loadAll();
        } finally {
            setActionLoading(false);
        }
    };

    // ──────────────────────────────────────────
    // HANDLERS ESPACIOS (ACORDEÓN)
    // ──────────────────────────────────────────
    const handleToggleEspacioActivo = async (item: LandingEspacio) => {
        try {
            const formData = new FormData();
            formData.append('activo', String(!item.activo));
            await updateEspacio(item.id, formData);
            setEspacios((prev) =>
                prev.map((e) => (e.id === item.id ? { ...e, activo: !e.activo } : e))
            );
            showMessage('success', `Espacio "${item.label}" actualizado.`);
        } catch {
            showMessage('error', 'Error al actualizar estado del espacio.');
        }
    };

    const handleMoveEspacio = async (index: number, direction: 'up' | 'down') => {
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= espacios.length) return;
        setActionLoading(true);
        const newItems = [...espacios];
        const temp = newItems[index];
        newItems[index] = newItems[targetIndex];
        newItems[targetIndex] = temp;
        const payload = newItems.map((item, idx) => ({ id: item.id, orden: idx + 1 }));
        setEspacios(newItems.map((item, idx) => ({ ...item, orden: idx + 1 })));
        try {
            await reorderEspacios(payload);
            showMessage('success', 'Nuevo orden de espacios guardado.');
        } catch {
            showMessage('error', 'Error al reordenar espacios.');
            loadAll();
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteEspacioConfirm = async () => {
        if (!selectedEspacio) return;
        try {
            setActionLoading(true);
            await deleteEspacio(selectedEspacio.id);
            setEspacios((prev) => prev.filter((e) => e.id !== selectedEspacio.id));
            showMessage('success', 'Espacio eliminado correctamente.');
            setIsDeleteEspacioOpen(false);
            setSelectedEspacio(null);
        } catch {
            showMessage('error', 'Error al eliminar espacio.');
        } finally {
            setActionLoading(false);
        }
    };

    // ──────────────────────────────────────────
    // HANDLERS CARRUSEL EXPERIENCIA
    // ──────────────────────────────────────────
    const handleToggleExperienciaActivo = async (item: LandingExperiencia) => {
        try {
            const formData = new FormData();
            formData.append('activo', String(!item.activo));
            await updateExperiencia(item.id, formData);
            setExperiencias((prev) =>
                prev.map((e) => (e.id === item.id ? { ...e, activo: !e.activo } : e))
            );
            showMessage('success', `Tarjeta "${item.titulo.replace('\n', ' ')}" actualizada.`);
        } catch {
            showMessage('error', 'Error al actualizar tarjeta.');
        }
    };

    const handleMoveExperiencia = async (index: number, direction: 'up' | 'down') => {
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= experiencias.length) return;
        setActionLoading(true);
        const newItems = [...experiencias];
        const temp = newItems[index];
        newItems[index] = newItems[targetIndex];
        newItems[targetIndex] = temp;
        const payload = newItems.map((item, idx) => ({ id: item.id, orden: idx + 1 }));
        setExperiencias(newItems.map((item, idx) => ({ ...item, orden: idx + 1 })));
        try {
            await reorderExperiencia(payload);
            showMessage('success', 'Nuevo orden del carrusel guardado.');
        } catch {
            showMessage('error', 'Error al reordenar carrusel.');
            loadAll();
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteExperienciaConfirm = async () => {
        if (!selectedExperiencia) return;
        try {
            setActionLoading(true);
            await deleteExperiencia(selectedExperiencia.id);
            setExperiencias((prev) => prev.filter((e) => e.id !== selectedExperiencia.id));
            showMessage('success', 'Slide de experiencia eliminado.');
            setIsDeleteExperienciaOpen(false);
            setSelectedExperiencia(null);
        } catch {
            showMessage('error', 'Error al eliminar slide.');
        } finally {
            setActionLoading(false);
        }
    };

    // ──────────────────────────────────────────
    // HANDLERS SECCIONES DIVIDIDAS (SPLIT)
    // ──────────────────────────────────────────
    const handleToggleSplitActivo = async (item: LandingSplitSectionItem) => {
        try {
            const formData = new FormData();
            formData.append('activo', String(!item.activo));
            await updateSplitSection(item.id, formData);
            setSplitSections((prev) =>
                prev.map((s) => (s.id === item.id ? { ...s, activo: !s.activo } : s))
            );
            showMessage('success', `Sección "${item.titulo}" actualizada.`);
        } catch {
            showMessage('error', 'Error al actualizar sección dividida.');
        }
    };

    const handleMoveSplit = async (index: number, direction: 'up' | 'down') => {
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= splitSections.length) return;
        setActionLoading(true);
        const newItems = [...splitSections];
        const temp = newItems[index];
        newItems[index] = newItems[targetIndex];
        newItems[targetIndex] = temp;
        const payload = newItems.map((item, idx) => ({ id: item.id, orden: idx + 1 }));
        setSplitSections(newItems.map((item, idx) => ({ ...item, orden: idx + 1 })));
        try {
            await reorderSplitSections(payload);
            showMessage('success', 'Nuevo orden de secciones guardado.');
        } catch {
            showMessage('error', 'Error al reordenar secciones.');
            loadAll();
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteSplitConfirm = async () => {
        if (!selectedSplit) return;
        try {
            setActionLoading(true);
            await deleteSplitSection(selectedSplit.id);
            setSplitSections((prev) => prev.filter((s) => s.id !== selectedSplit.id));
            showMessage('success', 'Sección informativa eliminada.');
            setIsDeleteSplitOpen(false);
            setSelectedSplit(null);
        } catch {
            showMessage('error', 'Error al eliminar sección.');
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return <Loading text="Cargando gestor de secciones del Landing Page..." />;
    }

    const specs:Record<ActiveTab,{title:string;rows:any[];name:string;columns:LumaColumn[];create:()=>void;edit:(r:any)=>void;remove:(r:any)=>void;toggle:(r:any)=>void;move:(i:number,d:'up'|'down')=>void}>={
hero:{title:'Hero banner',rows:slides,name:'titulo',columns:[{key:'titulo',label:'Título principal'},{key:'titulo_acento',label:'Texto con acento'},{key:'tipo',label:'Tipo de medio'},{key:'orden',label:'Orden'},{key:'activo',label:'Estado'}],create:()=>setIsAddSlideOpen(true),edit:r=>{setSelectedSlide(r);setIsEditSlideOpen(true)},remove:r=>{setSelectedSlide(r);setIsDeleteSlideOpen(true)},toggle:handleToggleSlideActivo,move:handleMoveSlide},
espacios:{title:'Galería de espacios',rows:espacios,name:'label',columns:[{key:'label',label:'Nombre del espacio'},{key:'alt',label:'Texto alternativo'},{key:'orden',label:'Orden'},{key:'activo',label:'Estado'}],create:()=>{setSelectedEspacio(null);setIsEspacioModalOpen(true)},edit:r=>{setSelectedEspacio(r);setIsEspacioModalOpen(true)},remove:r=>{setSelectedEspacio(r);setIsDeleteEspacioOpen(true)},toggle:handleToggleEspacioActivo,move:handleMoveEspacio},
experiencia:{title:'Experiencia',rows:experiencias,name:'titulo',columns:[{key:'titulo',label:'Título principal'},{key:'tag',label:'Etiqueta superior'},{key:'stat',label:'Cifra'},{key:'stat_label',label:'Etiqueta de la cifra'},{key:'activo',label:'Estado'}],create:()=>{setSelectedExperiencia(null);setIsExperienciaModalOpen(true)},edit:r=>{setSelectedExperiencia(r);setIsExperienciaModalOpen(true)},remove:r=>{setSelectedExperiencia(r);setIsDeleteExperienciaOpen(true)},toggle:handleToggleExperienciaActivo,move:handleMoveExperiencia},
split:{title:'Secciones de contenido',rows:splitSections,name:'titulo',columns:[{key:'titulo',label:'Título principal'},{key:'tag',label:'Etiqueta superior'},{key:'texto_boton',label:'Texto del botón'},{key:'orden',label:'Orden'},{key:'activo',label:'Estado'}],create:()=>{setSelectedSplit(null);setIsSplitModalOpen(true)},edit:r=>{setSelectedSplit(r);setIsSplitModalOpen(true)},remove:r=>{setSelectedSplit(r);setIsDeleteSplitOpen(true)},toggle:handleToggleSplitActivo,move:handleMoveSplit}};const spec=specs[activeTab];
return <><LumaResourceWorkspace title={spec.title} description="Administra el contenido de la página pública de Co Marca." group="Contenido" rows={spec.rows} columns={spec.columns} nameKey={spec.name} statusKey="activo" onCreate={spec.create} onEdit={spec.edit} onDelete={spec.remove} extraActions={r=><><button type="button" disabled={actionLoading} onClick={()=>spec.toggle(r)}>{r.activo?'Despublicar':'Publicar'}</button><button type="button" aria-label="Subir posición" disabled={actionLoading||spec.rows.indexOf(r)===0} onClick={()=>spec.move(spec.rows.indexOf(r),'up')}>↑</button><button type="button" aria-label="Bajar posición" disabled={actionLoading||spec.rows.indexOf(r)===spec.rows.length-1} onClick={()=>spec.move(spec.rows.indexOf(r),'down')}>↓</button></>}><nav className="luma-content-tabs">{[{tab:'hero',path:'diapositivas',label:'Hero banner'},{tab:'espacios',path:'galeria',label:'Galería de espacios'},{tab:'experiencia',path:'experiencia',label:'Experiencia'},{tab:'split',path:'secciones',label:'Secciones de contenido'}].map(t=><Link key={t.tab} aria-current={activeTab===t.tab?'page':undefined} to={'/banner-hero/'+t.path}>{t.label}</Link>)}</nav>{message&&<p role="status" className="luma-feedback">{message.text}</p>}</LumaResourceWorkspace><AddBannerSlideModal
                isOpen={isAddSlideOpen}
                onClose={() => setIsAddSlideOpen(false)}
                onSuccess={() => { loadAll(); showMessage('success', 'Diapositiva del Hero agregada.'); }}
                nextOrderNumber={slides.length + 1}
            /><EditBannerSlideModal
                isOpen={isEditSlideOpen}
                onClose={() => { setIsEditSlideOpen(false); setSelectedSlide(null); }}
                onSuccess={() => { loadAll(); showMessage('success', 'Diapositiva actualizada.'); }}
                slide={selectedSlide}
            /><DeleteBannerSlideModal
                isOpen={isDeleteSlideOpen}
                onClose={() => { setIsDeleteSlideOpen(false); setSelectedSlide(null); }}
                onSuccess={() => { loadAll(); showMessage('success', 'Diapositiva eliminada.'); }}
                slide={selectedSlide}
            /><EspacioModal
                isOpen={isEspacioModalOpen}
                onClose={() => { setIsEspacioModalOpen(false); setSelectedEspacio(null); }}
                onSuccess={() => { loadAll(); showMessage('success', 'Espacio guardado con éxito.'); }}
                espacioToEdit={selectedEspacio}
            /><DeleteLandingItemModal
                isOpen={isDeleteEspacioOpen}
                onClose={() => { setIsDeleteEspacioOpen(false); setSelectedEspacio(null); }}
                onConfirm={handleDeleteEspacioConfirm}
                title="Eliminar Espacio"
                itemName={selectedEspacio?.label || ''}
                loading={actionLoading}
            /><ExperienciaModal
                isOpen={isExperienciaModalOpen}
                onClose={() => { setIsExperienciaModalOpen(false); setSelectedExperiencia(null); }}
                onSuccess={() => { loadAll(); showMessage('success', 'Slide de experiencia guardado.'); }}
                itemToEdit={selectedExperiencia}
            /><DeleteLandingItemModal
                isOpen={isDeleteExperienciaOpen}
                onClose={() => { setIsDeleteExperienciaOpen(false); setSelectedExperiencia(null); }}
                onConfirm={handleDeleteExperienciaConfirm}
                title="Eliminar Slide de Experiencia"
                itemName={selectedExperiencia?.titulo || ''}
                loading={actionLoading}
            /><SplitSectionModal
                isOpen={isSplitModalOpen}
                onClose={() => { setIsSplitModalOpen(false); setSelectedSplit(null); }}
                onSuccess={() => { loadAll(); showMessage('success', 'Sección informativa guardada.'); }}
                itemToEdit={selectedSplit}
            /><DeleteLandingItemModal
                isOpen={isDeleteSplitOpen}
                onClose={() => { setIsDeleteSplitOpen(false); setSelectedSplit(null); }}
                onConfirm={handleDeleteSplitConfirm}
                title="Eliminar Sección Informativa"
                itemName={selectedSplit?.titulo || ''}
                loading={actionLoading}
            /></>;
};

export default BannerHeroAdminPage;


